import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ChevronRight, Clock, Sparkles, UserPlus, WifiOff } from 'lucide-react';
import type { MyTeam, TeamMember, TeamMemberDetail, TeamPost } from '@up2d8/contracts';
import {
  AppSubBar,
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  LoadingState,
  SearchInput,
} from '@up2d8/ui';
import { useOffline } from '../../offline/use-offline';
import { useSession } from '../../shared/session';
import { renderAppTabLink, useAppTabs } from './app-tabs';
import { DeskShell } from './desk/desk-shell';
import { MessagesIcon } from './messages-icon';
import { NotificationBell } from './notification-bell';
import { isAwaitingData } from '../../shared/query-state';
import { StartConversationButton, TeamPostPanel } from './app-team-post';
import {
  TEAM_SEARCH_FROM,
  membersOfTeam,
  useMyTeam,
  useTeamOpenCounts,
  useTeamPosts,
} from './use-my-team';

/**
 * Mijn team (Stap 17, TEAM-2).
 *
 * Het scherm van de ploegbaas: wie hoort bij zijn ploeg, en wie moet een verplicht bericht nog
 * bevestigen. Op een telefoon drie frames achter elkaar (overzicht, bericht, teamlid); vanaf
 * 900 px dezelfde indeling als Collega's — een lijst links, het gekozen ding rechts.
 *
 * **Niets uit de cache.** De spec is daar uitdrukkelijk over, en de reden is zwaarder dan ze
 * lijkt: "zeven van de negen bevestigd" van gisteren is erger dan geen antwoord, want de ploegbaas
 * handelt ernaar. Offline is dit scherm dus leeg, met één zin.
 *
 * **Geen zoekveld dat buiten het team zoekt** en geen niet-verplichte berichten (spec §5). Wat
 * iemand leest, blijft buiten het beeld van zijn leidinggevende; dat hij bevestigd heeft wat hij
 * moest bevestigen, staat erin.
 */
/**
 * De gekozen teamchip en "ook onderliggende groepen", over de routes van dit scherm heen.
 *
 * Overzicht, bericht en teamlid zijn drie routes met elk hun eigen boom; met alleen `useState`
 * sprong de keuze bij elke klik terug naar "Alle teams", en toonde een bericht de cijfers van alle
 * teams samen.
 */
const teamKeuze: { teamId: string | null; subgroups: boolean } = { teamId: null, subgroups: false };

export function AppTeamPage({
  selectedPostId = null,
  selectedPersonId = null,
  showAllPosts = false,
}: {
  selectedPostId?: string | null;
  selectedPersonId?: string | null;
  /** De route `/app/team/berichten`: alle berichten in plaats van de eerste drie. */
  showAllPosts?: boolean;
}) {
  const { t } = useTranslation();
  const tabs = useAppTabs();
  const navigate = useNavigate();
  const { online } = useOffline();
  const { has } = useSession();

  const [gekozenTeamId, setTeamIdState] = useState<string | null>(teamKeuze.teamId);
  const [subgroups, setSubgroupsState] = useState(teamKeuze.subgroups);
  const setTeamId = (waarde: string | null) => {
    teamKeuze.teamId = waarde;
    setTeamIdState(waarde);
  };
  const setSubgroups = (waarde: boolean) => {
    teamKeuze.subgroups = waarde;
    setSubgroupsState(waarde);
  };
  const [zoek, setZoek] = useState('');

  const team = useMyTeam({ subgroups });
  // Een onthouden team dat (na een wissel van organisatie) niet meer bij de kijker hoort, telt niet.
  const teamId =
    gekozenTeamId && team.data && !team.data.teams.some((groep) => groep.teamId === gekozenTeamId)
      ? null
      : gekozenTeamId;
  const posts = useTeamPosts(teamId);
  const open = useTeamOpenCounts(teamId);

  const leden = membersOfTeam(team.data, teamId);
  const openPer = open.data?.counts ?? {};

  const gefilterd = (lijst: TeamMember[]) =>
    zoek.trim().length === 0
      ? lijst
      : lijst.filter((lid) => lid.displayName.toLowerCase().includes(zoek.trim().toLowerCase()));

  const detail = selectedPostId ? (
    <TeamPostPanel postId={selectedPostId} teamId={teamId} />
  ) : selectedPersonId ? (
    <TeamMemberPanel personId={selectedPersonId} />
  ) : null;

  return (
    <DeskShell
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      tabs={tabs}
      activeKey="profile"
      renderLink={renderAppTabLink}
      aside={detail ?? undefined}
    >
      {/*
        Het gekozen bericht of teamlid staat in de rechterkolom van de schil, en die kolom valt op
        een smal scherm bovenaan de ene kolom (`DeskShell`, §6). Daarom staat het **één keer** in de
        DOM en niet twee keer met een `desk:hidden` ernaast: twee exemplaren zijn twee queries, twee
        mutaties, en twee keer dezelfde knop voor een schermlezer.

        Op een telefoon is een bericht een eigen frame, dus verdwijnt het overzicht zolang er iets
        gekozen is. Vanaf 900 px staan ze naast elkaar, precies zoals de mockup.
      */}
      <div
        className={
          detail ? 'hidden desk:flex desk:flex-col desk:gap-element' : 'flex flex-col gap-element'
        }
      >
        <AppSubBar title={t('team.title')} onBack={() => void navigate({ to: '/app/profile' })} />

        {!online ? (
          /*
            Offline: een volledig scherm en niets uit de cache (spec §4).

            Dit is het enige scherm in de app dat zo hard is. Elders laat de app zien wat ze heeft
            met een balk erboven; hier zou dat een cijfer over mensen zijn dat niet meer klopt.
          */
          <Card data-testid="team-offline">
            <EmptyState
              icon={<WifiOff aria-hidden className="h-8 w-8" />}
              title={t('team.offlineTitle')}
              description={t('team.offlineHint')}
            />
          </Card>
        ) : isAwaitingData(team) ? (
          // Zonder teamrecht haalt de query niets op en blijft ze `isPending` (#14).
          <LoadingState rows={6} label={t('common.loading')} />
        ) : (team.data?.teams.length ?? 0) === 0 ? (
          <Card data-testid="team-none">
            <EmptyState title={t('team.noTeamTitle')} description={t('team.noTeamHint')} />
          </Card>
        ) : (
          <>
            <TeamChips
              team={team.data}
              teamId={teamId}
              onChoose={setTeamId}
              subgroups={subgroups}
              onSubgroups={setSubgroups}
            />

            <section className="flex flex-col gap-element">
              <h2 className="font-heading text-title-sm font-semibold text-text-primary">
                {t('team.postsTitle')}
              </h2>

              {posts.isPending ? (
                <LoadingState rows={3} label={t('common.loading')} />
              ) : (posts.data?.items.length ?? 0) === 0 ? (
                <Card data-testid="team-posts-empty">
                  <EmptyState
                    icon={<Sparkles aria-hidden className="h-8 w-8" />}
                    title={t('team.postsEmpty')}
                  />
                </Card>
              ) : (
                <>
                  {/* Hoogstens drie kaarten; de rest staat achter "Alle berichten". */}
                  {(showAllPosts
                    ? (posts.data?.items ?? [])
                    : (posts.data?.items ?? []).slice(0, 3)
                  ).map((post) => (
                    <TeamPostCard
                      key={post.postId}
                      post={post}
                      selected={post.postId === selectedPostId}
                      onOpen={() =>
                        void navigate({
                          to: '/app/team/berichten/$postId',
                          params: { postId: post.postId },
                        })
                      }
                    />
                  ))}
                  {!showAllPosts && (posts.data?.items.length ?? 0) > 3 ? (
                    <Button
                      variant="ghost"
                      data-testid="team-all-posts"
                      onClick={() => void navigate({ to: '/app/team/berichten' })}
                    >
                      {t('team.allPosts')}
                    </Button>
                  ) : null}
                </>
              )}
            </section>

            <section className="flex flex-col gap-element">
              <h2 className="font-heading text-title-sm font-semibold text-text-primary">
                {t('team.membersTitle', { count: leden.active.length })}
              </h2>

              {/* Vanaf tien leden een zoekveld, en dat zoekt alleen binnen het team (spec §5). */}
              {leden.active.length >= TEAM_SEARCH_FROM ? (
                <Card>
                  <SearchInput
                    id="team-search"
                    label={t('team.search')}
                    value={zoek}
                    onChange={(event) => setZoek(event.target.value)}
                  />
                </Card>
              ) : null}

              <Card>
                <ul className="flex flex-col" data-testid="team-members">
                  {gefilterd(leden.active).map((lid) => (
                    <TeamMemberRow
                      key={lid.personId}
                      member={lid}
                      openCount={openPer[lid.personId] ?? lid.openMandatory}
                      onOpen={() =>
                        void navigate({
                          to: '/app/team/leden/$personId',
                          params: { personId: lid.personId },
                        })
                      }
                    />
                  ))}
                  {/* Gedeactiveerde leden onderaan, op 60 % dekking, en niet meegeteld. */}
                  {gefilterd(leden.deactivated).map((lid) => (
                    <TeamMemberRow
                      key={lid.personId}
                      member={lid}
                      openCount={0}
                      deactivated
                      onOpen={() =>
                        void navigate({
                          to: '/app/team/leden/$personId',
                          params: { personId: lid.personId },
                        })
                      }
                    />
                  ))}
                </ul>
              </Card>

              {/* Stap 31: een uitzendkracht toevoegen. Niet in de mockup (docs/design/deviations.md). */}
              {has('directory.temporary.create') ? (
                <Button
                  variant="secondary"
                  size="lg"
                  data-testid="team-add-temporary"
                  onClick={() => void navigate({ to: '/app/team/tijdelijk' })}
                >
                  <UserPlus aria-hidden className="h-5 w-5" />
                  {t('temporary.add')}
                </Button>
              ) : null}
            </section>
          </>
        )}
      </div>
    </DeskShell>
  );
}

/**
 * De teamkeuze als chips: "Ploeg B · 9", "Lijn 3 · 14".
 *
 * Alleen zichtbaar bij meer dan één team (spec §2): bij één team is een keuze tussen één ding geen
 * keuze maar ruis. De schakelaar "ook onderliggende groepen" staat eronder en alleen als er een
 * beheerde groep bij is — zonder groep valt er niets te nesten.
 */
function TeamChips({
  team,
  teamId,
  onChoose,
  subgroups,
  onSubgroups,
}: {
  team: MyTeam | undefined;
  teamId: string | null;
  onChoose: (id: string | null) => void;
  subgroups: boolean;
  onSubgroups: (value: boolean) => void;
}) {
  const { t } = useTranslation();
  const teams = team?.teams ?? [];
  if (teams.length <= 1) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2" data-testid="team-chips">
        <Button
          size="sm"
          variant={teamId === null ? 'primary' : 'secondary'}
          onClick={() => onChoose(null)}
        >
          {t('team.allTeams')}
        </Button>
        {teams.map((groep) => (
          <Button
            key={groep.teamId}
            size="sm"
            data-testid="team-chip"
            variant={teamId === groep.teamId ? 'primary' : 'secondary'}
            onClick={() => onChoose(groep.teamId)}
          >
            {groep.kind === 'reports' ? t('team.myReports') : groep.label} · {groep.members.length}
          </Button>
        ))}
      </div>

      {teams.some((groep) => groep.kind === 'group') ? (
        <label className="flex min-h-touch items-center gap-2 text-small text-text-secondary">
          <input
            type="checkbox"
            data-testid="team-subgroups"
            checked={subgroups}
            onChange={(event) => onSubgroups(event.target.checked)}
            className="h-5 w-5 rounded border-border-strong"
          />
          {t('team.includeSubgroups')}
        </label>
      ) : null}
    </div>
  );
}

/**
 * Eén bericht als kaart: titel, deadline, balk, "7 van 9 bevestigd".
 *
 * Bij een volledig bevestigd bericht een groene volle balk met het woord erbij, en geen vinkje
 * alleen: kleur is geen informatie voor wie haar niet ziet (ADR-0018).
 */
function TeamPostCard({
  post,
  selected,
  onOpen,
}: {
  post: TeamPost;
  selected: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const klaar = post.expected > 0 && post.acknowledged >= post.expected;
  const percentage =
    post.expected === 0 ? 0 : Math.round((post.acknowledged / post.expected) * 100);

  return (
    <Card compact className={selected ? 'border-brand-cyan' : undefined}>
      <button
        type="button"
        data-testid="team-post"
        onClick={onOpen}
        className="flex w-full items-center gap-element text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="block font-heading text-content font-semibold text-text-primary">
            {post.title}
          </span>

          {/*
            De cijfers vallen weg bij een klein team zonder leesbevestiging (ADR-0041): dan staat
            er één zin en geen balk. Dat is geen lege kaart maar het juiste antwoord.
          */}
          {post.countsSuppressed ? (
            <span className="mt-1 block text-small text-text-muted">{t('team.sentToTeam')}</span>
          ) : (
            <>
              <span className="mt-1 flex items-center gap-1 text-small text-text-muted">
                <Clock aria-hidden className="h-4 w-4" />
                {post.publishedAt ? t('team.published') : t('team.notPublished')}
              </span>
              <span
                className="mt-2 block h-2 w-full overflow-hidden rounded-full bg-surface-sunk"
                role="progressbar"
                aria-valuenow={percentage}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <span
                  className={`block h-full ${klaar ? 'bg-success' : 'bg-brand-primary'}`}
                  style={{ width: `${percentage}%` }}
                />
              </span>
              <span className="mt-1 flex items-center gap-1 text-small text-text-secondary">
                {klaar ? (
                  <>
                    <CheckCircle2 aria-hidden className="h-4 w-4 text-success" />
                    {t('team.everyoneConfirmed')}
                  </>
                ) : (
                  t('team.confirmedOf', {
                    done: post.acknowledged,
                    total: post.expected,
                  })
                )}
              </span>
            </>
          )}
        </span>
        <ChevronRight aria-hidden className="h-5 w-5 shrink-0 text-text-muted" />
      </button>
    </Card>
  );
}

/** Eén teamlid als rij: avatar, naam, functie, en een badge alleen als er iets is. */
function TeamMemberRow({
  member,
  openCount,
  deactivated = false,
  onOpen,
}: {
  member: TeamMember;
  openCount: number;
  deactivated?: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();

  return (
    <li
      data-testid="team-member"
      className={`border-b border-border-light last:border-b-0 ${deactivated ? 'opacity-60' : ''}`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex min-h-touch w-full items-center gap-element py-2 text-left"
      >
        <Avatar name={member.displayName} size="md" />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-heading text-content font-medium text-text-primary">
            {member.displayName}
          </span>
          {member.department || member.shift || member.site ? (
            <span className="block truncate text-small text-text-muted">
              {[member.department, member.shift, member.site].filter(Boolean).join(' · ')}
            </span>
          ) : null}
        </span>

        {/*
          Eén badge, en alleen als er iets te zeggen is. Een rij met drie badges leest als een
          dossier; de ploegbaas kijkt naar wie hij moet aanspreken.
        */}
        {deactivated ? (
          <Badge tone="neutral">{t('team.deactivated')}</Badge>
        ) : member.accountStatus !== 'active' ? (
          <Badge tone="neutral">{t('team.notActivated')}</Badge>
        ) : openCount > 0 ? (
          <Badge tone="orange">{t('team.openCount', { count: openCount })}</Badge>
        ) : null}

        <ChevronRight aria-hidden className="h-5 w-5 shrink-0 text-text-muted" />
      </button>
    </li>
  );
}

/**
 * Eén teamlid (frame 3): de profielkaart plus de blokken voor zijn leidinggevende.
 *
 * Staat in dit bestand en niet bij Collega's, omdat de blokken eronder iets anders zijn dan een
 * collegaprofiel: een accountstatus en openstaande bevestigingen zijn gegevens die alleen zijn
 * leidinggevende ziet.
 */
function TeamMemberPanel({ personId }: { personId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { api } = useSession();

  const detail = useQuery({
    queryKey: ['my-team-member', personId],
    staleTime: 0,
    queryFn: async (): Promise<TeamMemberDetail> => {
      const { data, error } = await api.GET('/api/v1/me/team/members/{personId}', {
        params: { path: { personId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as TeamMemberDetail;
    },
  });

  if (detail.isPending) return <LoadingState rows={4} label={t('common.loading')} />;
  if (!detail.data) {
    return (
      <Card>
        <EmptyState title={t('team.memberGone')} />
      </Card>
    );
  }

  const lid = detail.data.member;

  return (
    <div className="flex flex-col gap-element" data-testid="team-member-detail">
      <AppSubBar title={lid.displayName} onBack={() => void navigate({ to: '/app/team' })} />

      <Card>
        <div className="flex flex-col items-center gap-2 text-center">
          <Avatar name={lid.displayName} size="2xl" />
          <p className="font-heading text-page font-semibold text-text-primary">
            {lid.displayName}
          </p>
          <p className="text-small text-text-muted">
            {[lid.department, lid.shift, lid.site].filter(Boolean).join(' · ') || '—'}
          </p>
          <StartConversationButton personId={lid.personId} name={lid.displayName} />
        </div>
      </Card>

      <section className="flex flex-col gap-element">
        <h2 className="font-heading text-title-sm font-semibold text-text-primary">
          {t('team.forYouTitle')}
        </h2>

        <Card compact>
          <p className="text-caption uppercase tracking-[0.08em] text-text-muted">
            {t('team.accountTitle')}
          </p>
          <div className="mt-1 flex items-center gap-2">
            <Badge tone={lid.accountStatus === 'active' ? 'success' : 'neutral'}>
              {t(`team.account.${lid.accountStatus}`)}
            </Badge>
          </div>
          {lid.accountStatus !== 'active' ? (
            <p className="mt-1 text-small text-text-muted">{t('team.accountHint')}</p>
          ) : null}
        </Card>

        {/* De kaart Meldingen alleen met het recht; `null` is "ik mag het niet weten". */}
        {lid.push ? (
          <Card compact data-testid="team-member-push">
            <p className="text-caption uppercase tracking-[0.08em] text-text-muted">
              {t('team.pushTitle')}
            </p>
            <p className="mt-1 text-content text-text-primary">
              {lid.push.enabled ? t('team.pushOn') : t('team.pushOff')} ·{' '}
              {t('team.pushDevices', { count: lid.push.deviceCount })}
            </p>
          </Card>
        ) : null}
      </section>

      {/*
        De openstaande berichten, alleen als de namen mogen (ADR-0014). Mag het niet, dan staat er
        het aantal - dat mag altijd (ADR-0049) - en geen lege lijst.
      */}
      <section className="flex flex-col gap-element">
        <h2 className="font-heading text-title-sm font-semibold text-text-primary">
          {t('team.openPostsTitle')}
        </h2>
        {detail.data.openPosts === null ? (
          <Card compact data-testid="team-member-open-hidden">
            <p className="text-small text-text-secondary">
              {t('team.openCountOnly', { count: lid.openMandatory })}
            </p>
          </Card>
        ) : detail.data.openPosts.length === 0 ? (
          <Card compact>
            <p className="text-small text-text-muted">{t('team.openPostsEmpty')}</p>
          </Card>
        ) : (
          <Card compact>
            <ul className="flex flex-col divide-y divide-border-light">
              {detail.data.openPosts.map((post) => (
                <li key={post.postId} className="py-2">
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() =>
                      void navigate({
                        to: '/app/team/berichten/$postId',
                        params: { postId: post.postId },
                      })
                    }
                  >
                    <span className="block text-content text-text-primary">{post.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </section>
    </div>
  );
}

/**
 * De route `/app/team/berichten/$postId`.
 *
 * Een eigen component en geen `useParams` in `AppTeamPage`: dat scherm wordt ook zonder parameter
 * gebruikt, en een hook die soms niets vindt, is een hook die ergens een `undefined` doorlaat.
 */
export function AppTeamPostRoutePage() {
  const { postId } = useParams({ strict: false }) as { postId: string };
  return <AppTeamPage selectedPostId={postId} />;
}

/** De route `/app/team/berichten`: alle berichten van het team. */
export function AppTeamPostsRoutePage() {
  return <AppTeamPage showAllPosts />;
}

/** De route `/app/team/leden/$personId`. */
export function AppTeamMemberRoutePage() {
  const { personId } = useParams({ strict: false }) as { personId: string };
  return <AppTeamPage selectedPersonId={personId} />;
}
